import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtrasado } from '../agenda/useAtrasado.js';
import { alunosApi } from '../api/alunos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { Avatar, Feed, ItemDeFeed } from '../ui/primitivos.jsx';

/**
 * Escolher um aluno por busca — nome, telefone ou CPF.
 *
 * O mesmo padrão de "Matricular aluno" em `turmas/ModalDaTurma.tsx`: a
 * busca espera a pessoa parar de digitar, só consulta a partir de duas
 * letras e mostra poucas linhas. Cadastro anonimizado e aluno cancelado
 * não aparecem — não há quem atender nem a quem cobrar.
 *
 * O componente só entrega o escolhido; o que fazer com ele é de quem o usa.
 */
export function EscolhaDeAluno({
  onEscolher,
}: {
  onEscolher: (aluno: { id: string; nome: string }) => void;
}) {
  const idDoCampo = useId();
  const [busca, setBusca] = useState('');
  const termo = useAtrasado(busca.trim(), 300);
  const consultando = termo.length >= 2;

  const alunos = useQuery({
    queryKey: ['alunos', 'listar', 'escolha', termo],
    queryFn: () => alunosApi.listar({ busca: termo, limite: 6 }),
    enabled: consultando,
    retry: false,
  });

  const candidatos = (alunos.data?.itens ?? []).filter(
    (a) => !a.anonimizado && a.status !== 'Cancelado',
  );
  // Só afirma "ninguém" quando a resposta é da busca que está na tela.
  const semResultado =
    consultando && termo === busca.trim() && alunos.isSuccess && candidatos.length === 0;

  return (
    <div className="field">
      <label htmlFor={idDoCampo}>Aluno</label>
      <input
        id={idDoCampo}
        placeholder="Nome, telefone ou CPF"
        autoComplete="off"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
      />
      {alunos.isError && (
        <div className="help" style={{ color: 'var(--red-lt)' }} role="alert">
          {mensagemDoErro(alunos.error)}
        </div>
      )}
      {semResultado && (
        <div className="help" role="status">
          Nenhum aluno encontrado.
        </div>
      )}
      {candidatos.length > 0 && (
        <Feed>
          {candidatos.map((a) => (
            <ItemDeFeed
              key={a.id}
              marca={<Avatar nome={a.nome} />}
              titulo={a.nome}
              subtitulo={a.apelido ? `"${a.apelido}"` : undefined}
              onClick={() => onEscolher({ id: a.id, nome: a.nome })}
            />
          ))}
        </Feed>
      )}
    </div>
  );
}
